import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import ReactGA from "react-ga4";

import badges from "../../utils/badgeData";
import ProgressScore from "../GameScreen/ProgressScore";
import Button from "../Button";
import FunFactsModal from "../LandingScreen/FunFacts";
import JoinLeaderboardModal from "../GameScreen/JoinLeaderboardModal";

import {
  MessageBox,
  BadgeBox,
  BoxMessage,
  MessageImage,
  PageHeader,
  Wrapper,
  Spacer,
  ModalHeader,
} from "../MasterCss";

const FEEDBACK_KEY = "hasGivenFeedback"; // localStorage key

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  gap: 4vw;
  flex-wrap: wrap;
  width: 90vw;
  padding: 2vh 5vw 2vh 5vw;
  @media (max-width: 480px) {
    gap: 5vw;
    padding: 2vh 2vw 2vh 2vw;
  }
`;

const ScrollableContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding-bottom: 2vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;


const FeedbackOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 4000;
  backdrop-filter: blur(4px);
`;

const FeedbackModalBox = styled.div`
  background-image: linear-gradient(#21b2d3, #7abefd);
  width: 90vw;
  max-width: 600px;
  max-height: 85vh;
  overflow-y: auto;
  border-radius: 1em;
  border: 2px solid #08345c;
  filter: drop-shadow(4px 4px 4px #08345c);
  text-align: center;
  padding: 2rem 1.5rem;
  position: relative;
  @media (max-width: 480px) {
    padding: 1.5rem 1rem;
  }
  @media (max-width: 360px) {
    padding: 1rem 0.8rem;
  }
`;

const HeaderWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  position: relative;
  margin-bottom: 0.5rem;
`;

const FeedbackTitle = styled(ModalHeader)`
  color: #08345c;
  font-size: 2.4rem;
  margin: 0;
  @media (max-width: 480px) {
    font-size: 1.8rem;
  }
  @media (max-width: 360px) {
    font-size: 1.4rem;
  }
`;

const FeedbackSubtitle = styled.p`
  color: #08345c;
  font-size: 1.15rem;
  font-weight: 500;
  margin: 0 0 1.5rem 0;
  @media (max-width: 480px) {
    font-size: 1rem;
  }
  @media (max-width: 360px) {
    font-size: 0.85rem;
  }
`;

const EmojiRow = styled.div`
  display: flex;
  gap: 2vw;
  justify-content: center;
  flex-wrap: wrap;
  margin: 1.5rem 0;
  @media (max-width: 480px) {
    gap: 3vw;
  }
  @media (max-width: 360px) {
    gap: 4vw;
  }
`;

const EmojiButton = styled.button`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  border: 3px solid #ffffff;
  background-color: ${({ color }) => color};
  font-size: 3rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  transition: transform 0.2s, box-shadow 0.2s;
  aspect-ratio: 1/1;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
  &:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.3);
  }
  &:active {
    transform: scale(0.95);
  }
  @media (max-width: 480px) {
    width: 60px;
    height: 60px;
    font-size: 2.2rem;
  }
  @media (max-width: 360px) {
    width: 50px;
    height: 50px;
    font-size: 1.8rem;
  }
`;


const ResultScreen = (props) => {
  const { t } = useTranslation();
  const [badgeGiven, setBadgeGiven] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const [searchParams] = useSearchParams();
  const game = searchParams.get("game");
  const type = searchParams.get("type");
  const [showJoinModal, setShowJoinModal] = useState(false);

 
  const hasFeedbackBeenGiven = localStorage.getItem(FEEDBACK_KEY) === "true";
  const [showFeedbackModal, setShowFeedbackModal] = useState(!hasFeedbackBeenGiven);

  useEffect(() => {
    if (props.count >= 15) setBadgeGiven(4);
    else if (props.count >= 10) setBadgeGiven(3);
    else if (props.count >= 5) setBadgeGiven(2);
    else if (props.count >= 1) setBadgeGiven(1);
    else setBadgeGiven(0);
  }, [props.count]);

  const navigate = useNavigate();

  const goToCertificate = () => {
    navigate("/certificate", { state: { count: props.count, badgeGiven, feedback } });
  };

  const restartGame = () => {
    props.setCount(0);
    props.setBadCount(0);
    if (game) {
      navigate(`/${game}?type=${type}`, { state: { restart: true } });
    }
  };

  const handleFeedback = (value) => {
    setFeedback(value);
    setShowFeedbackModal(false);
    localStorage.setItem(FEEDBACK_KEY, "true"); 

    ReactGA.event({
      category: "Game Feedback",
      action: "emoji_selected",
      label: value,
      value: props.count,
      nonInteraction: false,
    });

    if (props.onFeedback) {
      props.onFeedback(value);
    }
  };

  const handleSkipFeedback = () => {
    setShowFeedbackModal(false);
    localStorage.setItem(FEEDBACK_KEY, "true");
  };

  return (
    <Wrapper>
      <ScrollableContent>
        <Spacer />
        <Spacer />
        <PageHeader nomargin>{t("result.title")}</PageHeader>
        <Spacer />
        <Spacer />
        <ProgressScore $results count={props.count} />
        <Spacer />
        <BadgeBox>
          <MessageImage
            src={badges[badgeGiven].src}
            alt="An animal badge to say well done!"
          />
          <MessageBox $results>
            <BoxMessage>
              {t(badges[badgeGiven].i18nKey, { count: props.count })}
            </BoxMessage>
          </MessageBox>
        </BadgeBox>
        <Spacer />

        <ButtonContainer>
          {props.funFactsModal && (
            <FunFactsModal handleClose={props.hideFunFactsModal} />
          )}
          <Button
            primaryB
            label={t("result.joinLeaderboard")}
            handleClick={() => setShowJoinModal(true)}
          />
          <Button
            primaryC
            label={t("result.getCertificate")}
            handleClick={goToCertificate}
          />
          <Button primary label={t("result.playAgain")} handleClick={restartGame} />
        </ButtonContainer>
        <Spacer />
      </ScrollableContent>


      {showFeedbackModal && (
        <FeedbackOverlay onClick={handleSkipFeedback}>
          <FeedbackModalBox onClick={(e) => e.stopPropagation()}>
            <HeaderWrapper>
              <FeedbackTitle>{t("result.feedbackQuestion")}</FeedbackTitle>
            </HeaderWrapper>
            <FeedbackSubtitle>{t("result.feedbackSubtitle")}</FeedbackSubtitle>
            <EmojiRow>
              <EmojiButton color="#4caf50" onClick={() => handleFeedback("up")}>
                👍
              </EmojiButton>
              <EmojiButton color="#ffeb3b" onClick={() => handleFeedback("neutral")}>
                😐
              </EmojiButton>
              <EmojiButton color="#f44336" onClick={() => handleFeedback("down")}>
                👎
              </EmojiButton>
            </EmojiRow>
            <Spacer />
            <Button label={t("result.skip")} handleClick={handleSkipFeedback} />
            <Spacer />
          </FeedbackModalBox>
        </FeedbackOverlay>
      )}

      {showJoinModal && (
        <JoinLeaderboardModal
          score={props.count}
          onClose={() => setShowJoinModal(false)}
        />
      )}
    </Wrapper>
  );
};

export default ResultScreen;